import { FormField } from "@avamae/formbuilder";
import { useField, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import _ from "lodash";
import { Renderers } from "./model";
import { customBoolean } from "./RenderFunctions/customBoolean";
import { customDate } from "./RenderFunctions/customDate";
import { customTextArea } from "./RenderFunctions/customTextArea";
import { customField } from "./model";
import { customString } from "./RenderFunctions/customString";
import { customMultiSelect } from "./RenderFunctions/customMultiSelect";
import { customSingleSelect } from "./RenderFunctions/customSingleSelect";
import { customInteger } from "./RenderFunctions/customInteger";
import { customCurrency } from "./RenderFunctions/customCurrency";
import { customFloat } from "./RenderFunctions/customFloat";
import useProgrammeFilter from "./useProgrammeFilter";
import { useSelector } from "react-redux";
import { selectParentPorgrammeId } from "reducers/customFields";

interface CustomFieldProps {
  field: any;
  readOnly: boolean;
  classWrapper?: string;
  bFiltered?: boolean;
  columns?: boolean;
}

export const renders: Renderers = {
  string: customString,
  multiselect: customMultiSelect,
  singleselect: customSingleSelect,
  boolean: customBoolean,
  textarea: customTextArea,
  datetime: customDate,
  integer: customInteger,
  float: customFloat,
  currency: customCurrency,
};

export const CustomFieldRender: React.FC<CustomFieldProps> = ({
  field,
  readOnly,
  classWrapper,
  bFiltered,
  columns,
}) => {
  const [relevantErrors, setRelevantErrors] = useState<
    (string | null)[] | null
  >(null);

  const { setFieldValue } = useFormikContext();
  const [{ value }] = useField(field.name);
  let parentProgrammeId = useSelector(selectParentPorgrammeId);

  const handleOnChange = (e: any, index: number) => {
    const tempValues = _.cloneDeep(value);
    e.preventDefault();
    tempValues[index] = { ...tempValues[index], value: e.target.value };
    setFieldValue(field.name, [...tempValues]);
  };

  const handleOnChangeCurrency = (
    parsedValue: string | number,
    index: number
  ) => {
    const tempValues = _.cloneDeep(value);

    if (parsedValue === "") {
      tempValues[index] = {
        ...tempValues[index],
        value: null,
      };
      setFieldValue(field.name, [...tempValues]);
      return;
    }

    try {
      if (typeof parsedValue === "string") {
        parsedValue = parseFloat(parsedValue);
      }
    } catch {
      console.log("Failed to convert to float in handleOnChangeCurrency ");

      try {
        if (typeof parsedValue === "string") {
          parsedValue = parseInt(parsedValue);
        }
      } catch {
        console.log("Failed to convert to int in handleOnChangeCurrency ");
      }
    }
    if (typeof parsedValue === "number") {
      tempValues[index] = {
        ...tempValues[index],
        value: parsedValue.toFixed(2),
      };
      setFieldValue(field.name, [...tempValues]);
    }
  };

  const formik = useFormikContext();

  useEffect(() => {
    if (
      formik &&
      formik.errors &&
      typeof formik.errors === "object" &&
      formik.errors.hasOwnProperty(field.name) &&
      formik.submitCount > 0
    ) {
      const tempErrors =
        formik.errors[field.name as keyof typeof formik.errors];
      setRelevantErrors(tempErrors);
    } else {
      setRelevantErrors(null);
    }
  }, [formik]);

  const filteredCustomFields = useProgrammeFilter(
    value,
    parentProgrammeId,
    readOnly,
    bFiltered
  );

  return (
    <div className="CustomFieldRenderer" onClick={e => e.stopPropagation()}>
      {bFiltered &&
      (window.location.pathname === "/projects" ||
        window.location.pathname === "/projectmanagement") &&
      !parentProgrammeId &&
      !readOnly ? (
        <div className="Text">Please select a parent programme</div>
      ) : filteredCustomFields &&
        Array.isArray(filteredCustomFields) &&
        filteredCustomFields.length === 0 ? (
        <div className="Text">No additional information required</div>
      ) : columns ? (
        <div
          className="ColumnsContainer"
          style={{ display: "flex", flexDirection: "row", columnGap: 15 }}
        >
          <div
            className="Column"
            style={{ flex: 1, display: "flex", flexDirection: "column" }}
          >
            {value &&
              Array.isArray(value) &&
              value.length >= 1 &&
              filteredCustomFields &&
              Array.isArray(filteredCustomFields) &&
              filteredCustomFields.length > 0 &&
              filteredCustomFields.map((customField, idx) => {
                if (idx % 2 !== 0) return null; // Skip odd indices for the first column
                const index = value.findIndex(
                  (element: customField) => element.id === customField.id
                );
                let renderFunction = customField.customFieldType.toLowerCase();
                let isMulti = false;
                if (renderFunction === "multiselect") {
                  isMulti = true;
                } else if (renderFunction === "singleselect") {
                  // renderFunction = "singleselect"; // Normal single select
                  renderFunction = "multiselect"; // Search single select
                }
                const render = renders[renderFunction];

                const relevantError =
                  relevantErrors &&
                  Array.isArray(relevantErrors) &&
                  relevantErrors.length - 1 >= index
                    ? relevantErrors[index]
                    : null;

                let finalHandleOnChange = handleOnChange;

                if (renderFunction === "currency") {
                  finalHandleOnChange = handleOnChangeCurrency;
                }

                return render ? (
                  <div
                    key={`${customField.id}-${customField.name}-${idx}`}
                    className={`FieldItem ${relevantError ? "Error" : ""}`}
                  >
                    {render(
                      customField,
                      index,
                      readOnly,
                      finalHandleOnChange,
                      field,
                      isMulti,
                      classWrapper
                    )}

                    {relevantError && (
                      <div className="Error">{relevantError}</div>
                    )}
                  </div>
                ) : (
                  <div className="Error">renderer not found</div>
                );
              })}
          </div>
          <div
            className="Column"
            style={{ flex: 1, display: "flex", flexDirection: "column" }}
          >
            {value &&
              Array.isArray(value) &&
              value.length >= 1 &&
              filteredCustomFields &&
              Array.isArray(filteredCustomFields) &&
              filteredCustomFields.length > 0 &&
              filteredCustomFields.map((customField, idx) => {
                if (idx % 2 === 0) return null; // Skip even indices for the second column
                const index = value.findIndex(
                  (element: customField) => element.id === customField.id
                );
                let renderFunction = customField.customFieldType.toLowerCase();
                let isMulti = false;
                if (renderFunction === "multiselect") {
                  isMulti = true;
                } else if (renderFunction === "singleselect") {
                  // renderFunction = "singleselect"; // Normal single select
                  renderFunction = "multiselect"; // Search single select
                }
                const render = renders[renderFunction];

                const relevantError =
                  relevantErrors &&
                  Array.isArray(relevantErrors) &&
                  relevantErrors.length - 1 >= index
                    ? relevantErrors[index]
                    : null;

                let finalHandleOnChange = handleOnChange;

                if (renderFunction === "currency") {
                  finalHandleOnChange = handleOnChangeCurrency;
                }

                return render ? (
                  <div
                    key={`${customField.id}-${customField.name}-${idx}`}
                    className={`FieldItem ${relevantError ? "Error" : ""}`}
                  >
                    {render(
                      customField,
                      index,
                      readOnly,
                      finalHandleOnChange,
                      field,
                      isMulti,
                      classWrapper
                    )}

                    {relevantError && (
                      <div className="Error">{relevantError}</div>
                    )}
                  </div>
                ) : (
                  <div className="Error">renderer not found</div>
                );
              })}
          </div>
        </div>
      ) : (
        value &&
        Array.isArray(value) &&
        value.length >= 1 &&
        filteredCustomFields &&
        Array.isArray(filteredCustomFields) &&
        filteredCustomFields.length > 0 &&
        filteredCustomFields.map((customField: customField, idx) => {
          const index = value.findIndex(
            (element: customField) => element.id === customField.id
          );
          let renderFunction = customField.customFieldType.toLowerCase();
          let isMulti = false;
          if (renderFunction === "multiselect") {
            isMulti = true;
          } else if (renderFunction === "singleselect") {
            // renderFunction = "singleselect"; // Normal single select
            renderFunction = "multiselect"; // Search single select
          }
          const render = renders[renderFunction];

          const relevantError =
            relevantErrors &&
            Array.isArray(relevantErrors) &&
            relevantErrors.length - 1 >= index
              ? relevantErrors[index]
              : null;

          let finalHandleOnChange = handleOnChange;

          if (renderFunction === "currency") {
            finalHandleOnChange = handleOnChangeCurrency;
          }

          return render ? (
            <div
              key={`${customField.id}-${customField.name}-${idx}`}
              className={`FieldItem ${relevantError ? "Error" : ""}`}
            >
              {render(
                customField,
                index,
                readOnly,
                finalHandleOnChange,
                field,
                isMulti,
                classWrapper
              )}

              {relevantError && <div className="Error">{relevantError}</div>}
            </div>
          ) : (
            <div className="Error">renderer not found</div>
          );
        })
      )}
    </div>
  );
};

export const customFieldRender = (readOnly: boolean, bFiltered?: boolean) =>
  function customFieldRenders<T>(_key: React.ReactText, field: FormField<T>) {
    return (
      <CustomFieldRender
        field={field}
        readOnly={readOnly}
        bFiltered={bFiltered}
      />
    );
  };

export default CustomFieldRender;

type FnProps = {
  bFiltered: boolean;
  parentProgrammeId?: string;
  readOnly: boolean;
  filteredCustomFields: customField[];
  value: Array<{ id: number }>;
  relevantErrors?: (string | null)[];
  renders: {
    [key: string]: (
      customField: customField,
      index: number,
      readOnly: boolean,
      finalHandleOnChange: Function,
      field: any,
      isMulti: boolean,
      classWrapper: string
    ) => JSX.Element;
  };
  handleOnChange: Function;
  handleOnChangeCurrency: Function;
  field: any; // Adjust according to your actual 'field' type
  classWrapper: string;
};

const CustomFieldRendererFunction: React.FC<FnProps> = ({
  bFiltered,
  parentProgrammeId,
  readOnly,
  filteredCustomFields,
  value,
  relevantErrors,
  renders,
  handleOnChange,
  handleOnChangeCurrency,
  field,
  classWrapper,
}) => {
  // Helper function to render a custom field
  const renderCustomField = (
    customField: customField,
    idx: number
  ): JSX.Element | null => {
    const index = value.findIndex(element => element.id === customField.id);
    let renderFunction = customField.customFieldType.toLowerCase();
    let isMulti = false;
    if (renderFunction === "multiselect") {
      isMulti = true;
    } else if (renderFunction === "singleselect") {
      // renderFunction = "singleselect"; // Normal single select
      renderFunction = "multiselect"; // Search single select
    }
    const render = renders[renderFunction];

    const relevantError =
      relevantErrors &&
      Array.isArray(relevantErrors) &&
      relevantErrors.length - 1 >= index
        ? relevantErrors[index]
        : null;

    let finalHandleOnChange = handleOnChange;

    if (renderFunction === "currency") {
      finalHandleOnChange = handleOnChangeCurrency;
    }

    return render ? (
      <div
        key={`${customField.id}-${customField.name}-${idx}`}
        className={`FieldItem ${relevantError ? "Error" : ""}`}
      >
        {render(
          customField,
          index,
          readOnly,
          finalHandleOnChange,
          field,
          isMulti,
          classWrapper
        )}
        {relevantError && <div className="Error">{String(relevantError)}</div>}
      </div>
    ) : (
      <div key={idx} className="Error">
        Renderer not found for {customField.customFieldType}
      </div>
    );
  };

  return (
    <div className="CustomFieldRenderer" onClick={e => e.stopPropagation()}>
      {/* Render logic remains the same, utilize renderCustomField within two column divs */}
      <div
        className="ColumnsContainer"
        style={{ display: "flex", flexDirection: "row" }}
      >
        <div className="Column" style={{ flex: 1 }}>
          {filteredCustomFields
            .filter((_, idx) => idx % 2 === 0)
            .map(renderCustomField)}
        </div>
        <div className="Column" style={{ flex: 1 }}>
          {filteredCustomFields
            .filter((_, idx) => idx % 2 !== 0)
            .map(renderCustomField)}
        </div>
      </div>
    </div>
  );
};
